import { useState } from 'react';
import {
  Alert,
  App,
  Button,
  Card,
  Form,
  Input,
  Modal,
  Select,
  Space,
  Tag,
  Typography,
} from 'antd';
import {
  LockOutlined,
  PoweroffOutlined,
  KeyOutlined,
  EyeInvisibleOutlined,
  EyeOutlined,
  ClearOutlined,
  SyncOutlined,
  AppstoreOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { useCreateCommand } from '@/hooks/useCommands';
import type { DeviceDto } from '@/types/device';
import type { CommandType } from '@/types/command';
import { CommandTypeLabels, CommandTypeMap } from '@/types/command';

const { Text, Paragraph } = Typography;

interface Props {
  device: DeviceDto | null;
  onClose: () => void;
}

const COMMAND_OPTIONS: {
  type: CommandType;
  label: string;
  icon: React.ReactNode;
  description: string;
  destructive?: boolean;
  needsParams?: boolean;
}[] = [
  { type: 'Lock', label: 'Bloquear pantalla', icon: <LockOutlined />, description: 'Bloquea inmediatamente la pantalla del dispositivo.' },
  { type: 'Reboot', label: 'Reiniciar', icon: <PoweroffOutlined />, description: 'Reinicia el dispositivo.' },
  { type: 'ResetPassword', label: 'Resetear PIN', icon: <KeyOutlined />, description: 'Resetea el PIN/passcode del dispositivo.', needsParams: true },
  { type: 'StartLostMode', label: 'Modo perdido', icon: <EyeInvisibleOutlined />, description: 'Activa el modo perdido (kiosko de bloqueo total).', destructive: true },
  { type: 'StopLostMode', label: 'Salir de modo perdido', icon: <EyeOutlined />, description: 'Saca al dispositivo del modo perdido.' },
  { type: 'ClearAppData', label: 'Limpiar datos de app', icon: <ClearOutlined />, description: 'Borra los datos de una app especifica.', needsParams: true },
  { type: 'Sync', label: 'Forzar sync', icon: <SyncOutlined />, description: 'Fuerza al device a reportar su estado a Google.' },
  { type: 'Wipe', label: 'Borrado total', icon: <WarningOutlined />, description: 'Resetea el dispositivo a fabrica. NO REVERSIBLE.', destructive: true },
];

const TYPE_TO_NUMBER: Record<CommandType, number> = (() => {
  const r: any = {};
  for (const [k, v] of Object.entries(CommandTypeMap)) r[v] = Number(k);
  return r;
})();

export function CreateCommandModal({ device, onClose }: Props) {
  const { message } = App.useApp();
  const [step, setStep] = useState<'select' | 'params' | 'confirm'>('select');
  const [selectedType, setSelectedType] = useState<CommandType | null>(null);
  const [paramsJson, setParamsJson] = useState<string>('');
  const [paramFormValues, setParamFormValues] = useState<Record<string, any>>({});

  const createMut = useCreateCommand();

  const handleClose = () => {
    setStep('select');
    setSelectedType(null);
    setParamsJson('');
    setParamFormValues({});
    onClose();
  };

  const handleSelect = (type: CommandType) => {
    setSelectedType(type);
    const opt = COMMAND_OPTIONS.find((o) => o.type === type)!;
    if (opt.needsParams) {
      setStep('params');
    } else {
      setStep('confirm');
    }
  };

  const handleParamsConfirm = () => {
    if (!selectedType) return;

    let json = '';
    if (selectedType === 'ResetPassword') {
      json = JSON.stringify({ newPassword: paramFormValues.newPassword || device?.expectedPin || '0321' });
    } else if (selectedType === 'ClearAppData') {
      if (!paramFormValues.packageName) {
        message.warning('Indica el package name de la app.');
        return;
      }
      json = JSON.stringify({ packageName: paramFormValues.packageName.trim() });
    }

    setParamsJson(json);
    setStep('confirm');
  };

  const handleConfirm = async () => {
    if (!device || !selectedType) return;
    try {
      await createMut.mutateAsync({
        deviceId: device.id,
        commandType: TYPE_TO_NUMBER[selectedType],
        parametersJson: paramsJson || undefined,
      });
      message.success(`Comando "${CommandTypeLabels[selectedType]}" enviado.`);
      handleClose();
    } catch {
      // interceptor
    }
  };

  const opt = selectedType ? COMMAND_OPTIONS.find((o) => o.type === selectedType) : null;

  return (
    <Modal
      title={device ? `Comando para: ${device.assignedToName ?? device.serialNumber ?? `Device #${device.id}`}` : 'Comando remoto'}
      open={device != null}
      onCancel={handleClose}
      footer={
        step === 'select'
          ? null
          : [
              <Button key="back" onClick={() => setStep(step === 'confirm' ? (opt?.needsParams ? 'params' : 'select') : 'select')}>
                Atras
              </Button>,
              ...(step === 'params'
                ? [<Button key="next" type="primary" onClick={handleParamsConfirm}>Continuar</Button>]
                : [
                    <Button
                      key="confirm"
                      type="primary"
                      danger={opt?.destructive}
                      loading={createMut.isPending}
                      onClick={handleConfirm}
                    >
                      Enviar comando
                    </Button>,
                  ]),
            ]
      }
      width={600}
      destroyOnClose
    >
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="Los comandos quedan en cola hasta que llegue la cuota AMAPI"
        description="Por ahora los comandos se guardan en la BD con estado Pending. Cuando llegue la cuota de Google, un job los enviara al dispositivo automaticamente."
      />

      {step === 'select' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          {COMMAND_OPTIONS.map((c) => (
            <Card
              key={c.type}
              hoverable
              size="small"
              onClick={() => handleSelect(c.type)}
              style={{
                cursor: 'pointer',
                borderColor: c.destructive ? 'var(--color-error)' : undefined,
              }}
            >
              <Space>
                <span style={{ fontSize: 24, color: c.destructive ? 'var(--color-error)' : 'var(--color-primary)' }}>
                  {c.icon}
                </span>
                <div>
                  <Text strong>{c.label}</Text>
                  {c.destructive && <Tag color="error" style={{ marginLeft: 8 }}>Critico</Tag>}
                  <Paragraph type="secondary" style={{ margin: 0, fontSize: 12 }}>
                    {c.description}
                  </Paragraph>
                </div>
              </Space>
            </Card>
          ))}
        </div>
      )}

      {step === 'params' && selectedType === 'ResetPassword' && (
        <Form layout="vertical">
          <Form.Item label="Nuevo PIN" required extra={`Por defecto: ${device?.expectedPin ?? '0321'}`}>
            <Input
              placeholder={device?.expectedPin ?? '0321'}
              value={paramFormValues.newPassword ?? ''}
              onChange={(e) => setParamFormValues({ newPassword: e.target.value })}
              maxLength={20}
              autoFocus
            />
          </Form.Item>
        </Form>
      )}

      {step === 'params' && selectedType === 'ClearAppData' && (
        <Form layout="vertical">
          <Form.Item label="Package name de la app" required>
            <Input
              placeholder="com.whatsapp"
              value={paramFormValues.packageName ?? ''}
              onChange={(e) => setParamFormValues({ packageName: e.target.value })}
              autoFocus
            />
          </Form.Item>
        </Form>
      )}

      {step === 'confirm' && opt && (
        <div>
          <Card size="small">
            <Space direction="vertical" style={{ width: '100%' }}>
              <Space>
                <span style={{ fontSize: 24, color: opt.destructive ? 'var(--color-error)' : 'var(--color-primary)' }}>
                  {opt.icon}
                </span>
                <Text strong style={{ fontSize: 16 }}>{opt.label}</Text>
                {opt.destructive && <Tag color="error">CRITICO</Tag>}
              </Space>
              <Paragraph>{opt.description}</Paragraph>
              {paramsJson && (
                <div>
                  <Text type="secondary">Parametros:</Text>
                  <pre style={{
                    background: 'var(--color-bg-elevated)',
                    padding: 8,
                    borderRadius: 4,
                    fontSize: 12,
                    margin: 0,
                  }}>
                    {JSON.stringify(JSON.parse(paramsJson), null, 2)}
                  </pre>
                </div>
              )}
            </Space>
          </Card>

          {opt.destructive && (
            <Alert
              type="warning"
              showIcon
              style={{ marginTop: 16 }}
              message="Operacion potencialmente destructiva"
              description={
                selectedType === 'Wipe'
                  ? 'El borrado total resetea el dispositivo a fabrica. Toda la data se pierde y el device debera re-enrolarse desde cero.'
                  : 'Esta accion bloqueara completamente el dispositivo hasta que se ejecute el comando inverso.'
              }
            />
          )}
        </div>
      )}
    </Modal>
  );
}
